/**
 * SprintPlanModal Component
 * AIDLC Administrative Backend
 *
 * Modal for creating a new sprint. Allows setting name, goal, dates,
 * and selecting backlog items to include.
 */

import { useState, useEffect } from 'react';
import { Modal } from '../../shared/Modal';
import { Button } from '../../shared/Button';
import { createSprint } from '../../../services/sprintService';
import { getBacklogItems } from '../../../services/backlogService';
import type { BacklogItem } from '../../../types/backlog';
import type { Sprint } from '../../../types/projects';

interface SprintPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onCreated: (sprint: Sprint) => void;
}

export function SprintPlanModal({ isOpen, onClose, projectId, onCreated }: SprintPlanModalProps) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [backlogItems, setBacklogItems] = useState<BacklogItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load unassigned backlog items for this project
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    async function load() {
      setIsLoadingItems(true);
      try {
        const items = await getBacklogItems({ project: projectId });
        if (!cancelled) {
          // Show items not yet assigned to a sprint, or items in new/open status
          const available = items.filter(
            (i) => !i.sprintId && i.status !== 'done' && i.status !== 'closed'
          );
          setBacklogItems(available);
        }
      } catch (err) {
        console.error('[SprintPlanModal] Failed to load backlog items:', err);
      } finally {
        if (!cancelled) setIsLoadingItems(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  // Set default dates (today + 2 weeks)
  useEffect(() => {
    if (isOpen && !startDate) {
      const today = new Date();
      const twoWeeks = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
      setStartDate(today.toISOString().split('T')[0]);
      setEndDate(twoWeeks.toISOString().split('T')[0]);
    }
  }, [isOpen, startDate]);

  const toggleItem = (itemId: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleSubmit = async () => {
    if (!name.trim() || !startDate || !endDate) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const sprint = await createSprint({
        projectId,
        name: name.trim(),
        goal: goal.trim(),
        startDate,
        endDate,
        backlogItemIds: selectedItemIds,
      });
      onCreated(sprint);
      // Reset form
      setName('');
      setGoal('');
      setStartDate('');
      setEndDate('');
      setSelectedItemIds([]);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create sprint');
    } finally {
      setIsSubmitting(false);
    }
  };

  const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortedItems = [...backlogItems].sort(
    (a, b) => (priorityOrder[a.priority] ?? 4) - (priorityOrder[b.priority] ?? 4)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Plan New Sprint"
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={!name.trim() || !startDate || !endDate}
          >
            Create Sprint
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-xs">{error}</p>
          </div>
        )}

        {/* Sprint details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Sprint Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="e.g. Sprint 3 - API Layer"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Sprint Goal</label>
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="What should the team achieve this sprint?"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
            />
          </div>
        </div>

        {/* Backlog item selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Select Backlog Items ({selectedItemIds.length} selected)
          </label>
          <div className="border border-gray-200 rounded-lg max-h-[240px] overflow-y-auto">
            {isLoadingItems ? (
              <div className="p-4 text-center text-xs text-gray-400">Loading backlog items...</div>
            ) : sortedItems.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400">No unassigned backlog items for this project.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {sortedItems.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedItemIds.includes(item.id)}
                      onChange={() => toggleItem(item.id)}
                      className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{item.title}</div>
                      <div className="text-[10px] text-gray-400">
                        {item.priority} | {item.status} | {item.assignedAgent || 'Unassigned'}
                      </div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      item.priority === 'critical' ? 'bg-red-50 text-red-700' :
                      item.priority === 'high' ? 'bg-yellow-50 text-yellow-700' :
                      item.priority === 'medium' ? 'bg-blue-50 text-ldaja-blue' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {item.priority}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
